import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IWorkerData } from '@th-types/worker.type';
import * as api from '@company/services/chat/api';
import IMessageData, {
  ISortableMessages,
  ISystemMessageData,
} from '@th-types/message.type';
import { JobChat } from '@company/types/comm-hub.type';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import {
  handleMarkAsReadOptimisticUpdate,
  handleMarkAsReadOptimisticRollback,
  handleMarkAsUnreadOptimisticUpdate,
  handleMarkAsUnreadOptimisticRollback,
} from '@utils/ComHubUtils';

// TODO: add jobChatId, which should replace chatAssignmentId
interface IChatContext {
  chatPerson: IWorkerData | null;
  chatAssignmentId: number | null;
  jobChatId: number | null;
  showPrivateInfo: boolean;
  messages: IMessageData[];
  systemMessages: ISystemMessageData[];
  unsentMessages: IMessageData[];
  orderedMessages: ISortableMessages[];
  /** The original message data (required for existing chats to enable optimistic updates when closing) */
  originalJobMessage: JobChat | null;
  chatWasCreated: boolean;
  setMessages: (messages: IMessageData[]) => void;
  setSystemMessages: (messages: ISystemMessageData[]) => void;
  setUnsentMessages: (messages: IMessageData[]) => void;
  setChatAssignmentId: (chatAssignmentId: number | null) => void;
  setJobChatId: (jobChatId: number | null) => void;
  setChatWasCreated: (created: boolean) => void;
  openChat: (
    person: IWorkerData,
    chatId: number | null,
    showWorkerInfo: boolean,
    jobChat?: JobChat | null,
    onSuccess?: () => void
  ) => void;
  markAsRead: (chatId: number, onSuccess?: () => void) => void;
  markAsUnread: (chatId: number, onSuccess?: () => void) => void;
  batchMarkAsRead: (chatIds: number[], onSuccess?: () => void) => void;
  clearChatData: () => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const ChatContext = createContext<IChatContext>({
  chatPerson: null,
  chatAssignmentId: null,
  jobChatId: null,
  showPrivateInfo: false,
  messages: [],
  systemMessages: [],
  unsentMessages: [],
  orderedMessages: [],
  originalJobMessage: null,
  chatWasCreated: false,
  setMessages: () => {},
  setSystemMessages: () => {},
  setUnsentMessages: () => {},
  setChatAssignmentId: () => {},
  setJobChatId: () => {},
  setChatWasCreated: () => {},
  openChat: () => {},
  markAsRead: () => {},
  markAsUnread: () => {},
  batchMarkAsRead: () => {},
  clearChatData: () => {},
});

export function ChatProvider({ children }: ProviderProps) {
  const [chatPerson, setChatPerson] = useState<IWorkerData | null>(null);
  const [chatAssignmentId, setChatAssignmentId] = useState<number | null>(null);
  const [jobChatId, setJobChatId] = useState<number | null>(null);
  const [showPrivateInfo, setShowPrivateInfo] = useState<boolean>(false);
  const [chatWasCreated, setChatWasCreated] = useState<boolean>(false);
  const [messages, setMessages] = useState<IMessageData[]>([]);
  const [systemMessages, setSystemMessages] = useState<ISystemMessageData[]>(
    []
  );
  const [unsentMessages, setUnsentMessages] = useState<IMessageData[]>([]);
  const [orderedMessages, setOrderedMessages] = useState<ISortableMessages[]>(
    []
  );
  const [originalJobChat, setOriginalJobChat] = useState<JobChat | null>(null);

  const queryClient = useQueryClient();

  // Mutation for marking messages as read (with count update)
  const readMessagesMutation = useMutation({
    mutationFn: api.readMessages,
    onMutate: async (chatId: number) => {
      return handleMarkAsReadOptimisticUpdate(queryClient, chatId, 'job');
    },
    onError: (err: any, assignmentId, context) => {
      if (context?.previousStates) {
        handleMarkAsReadOptimisticRollback(queryClient, context.previousStates);
      }
      // Log error but don't crash - the item may have been deleted
      console.error(
        `Failed to mark job chat ${assignmentId} as read:`,
        err?.response?.data || err
      );
    },
    onSettled: () => {
      // Invalidate job chat queries - use individual counter instead of indicators
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ComHub.JOB_CHAT] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.JOB_CHAT_COUNT],
      });
    },
  });

  // Mutation for opening chat (list and count update)
  const openChatMutation = useMutation({
    mutationFn: api.readMessages,
    onMutate: async (chatId: number) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.ComHub.JOB_CHAT],
      });

      return handleMarkAsReadOptimisticUpdate(queryClient, chatId, 'job');
    },
    onError: (err: any, assignmentId, context) => {
      if (context?.previousStates) {
        handleMarkAsReadOptimisticRollback(queryClient, context.previousStates);
      }
      // Log error but don't crash - the item may have been deleted
      console.error(
        `Failed to mark job chat ${assignmentId} as read when opening:`,
        err?.response?.data || err
      );
    },
    onSettled: () => {
      // Invalidate job chat count - use individual counter instead of indicators
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.JOB_CHAT_COUNT],
      });
    },
  });

  const unreadMessagesMutation = useMutation({
    mutationFn: api.unreadMessages,
    onMutate: async (chatId: number) => {
      return handleMarkAsUnreadOptimisticUpdate(queryClient, chatId, 'job');
    },
    onError: (err: any, assignmentId, context) => {
      if (context?.previousStates) {
        handleMarkAsUnreadOptimisticRollback(
          queryClient,
          context.previousStates
        );
      }
      // Log error but don't crash - the item may have been deleted
      console.error(
        `Failed to mark job chat ${assignmentId} as unread:`,
        err?.response?.data || err
      );
    },
    onSettled: () => {
      // Invalidate job chat queries - use individual counter instead of indicators
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ComHub.JOB_CHAT] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.JOB_CHAT_COUNT],
      });
    },
  });

  // Batch mutations for performance optimization
  const batchReadMessagesMutation = useMutation({
    mutationFn: api.batchReadMessages,
    onMutate: async (chatIds: number[]) => {
      // Apply optimistic updates for all chat IDs
      const previousStates: Array<{
        previousStates: Array<{ queryKey: unknown; data: unknown }>;
      }> = [];

      chatIds.forEach((chatId) => {
        const context = handleMarkAsReadOptimisticUpdate(
          queryClient,
          chatId,
          'job'
        );
        previousStates.push(context);
      });

      return { previousStates };
    },
    onError: (_, __, context) => {
      if (context?.previousStates) {
        // Rollback all optimistic updates
        context.previousStates.forEach(({ previousStates }) => {
          if (previousStates) {
            handleMarkAsReadOptimisticRollback(queryClient, previousStates);
          }
        });
      }
    },
    onSettled: () => {
      // Invalidate job chat queries - use individual counter instead of indicators
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ComHub.JOB_CHAT] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ComHub.JOB_CHAT_COUNT],
      });
    },
  });

  useEffect(() => {
    let chatMsgs: ISortableMessages[] = messages.map((m) => ({
      id: m.id,
      created: m.created,
      createdTimestamp: new Date(m.created.toLocaleString()).valueOf(),
      message: m,
      sent: m.sent === undefined || m.sent === true,
    }));

    chatMsgs = chatMsgs.concat(
      systemMessages.map((m) => ({
        id: m.id,
        created: m.created,
        createdTimestamp: new Date(m.created.toLocaleString()).valueOf(),
        systemMessage: m,
        sent: true,
      }))
    );

    chatMsgs = chatMsgs.concat(
      unsentMessages.map((m) => ({
        id: m.id,
        created: m.created,
        createdTimestamp: new Date(m.created.toLocaleString()).valueOf(),
        message: m,
        sent: false,
      }))
    );
    const unsentList = chatMsgs
      .filter((x) => !x.sent)
      .sort(
        (a, b) =>
          new Date(a.createdTimestamp).getTime() -
          new Date(b.createdTimestamp).getTime()
      );

    const allSent = chatMsgs
      .filter((x) => x.sent)
      .sort(
        (a, b) =>
          new Date(a.createdTimestamp).getTime() -
          new Date(b.createdTimestamp).getTime()
      );

    const all = allSent.concat(unsentList);
    setOrderedMessages(all);
  }, [messages, setMessages, systemMessages, unsentMessages]);

  const clearChatData = useCallback(() => {
    // No refetch on close - talents cache was already updated optimistically
    setChatPerson(null);
    setChatAssignmentId(null);
    setJobChatId(null);
    setOriginalJobChat(null);
    setChatWasCreated(false);
  }, []);

  const handleSetChatWasCreated = useCallback((created: boolean) => {
    setChatWasCreated(created);
  }, []);

  const openChat = useCallback(
    (
      person: IWorkerData,
      id: number | null,
      showWorkerInfo: boolean,
      jobChat?: JobChat | null,
      onSuccess?: () => void
    ) => {
      setChatPerson(person);
      // When opening from comm hub, id is the jobChatId, so set both states
      setJobChatId(id);
      setChatAssignmentId(id);
      setShowPrivateInfo(showWorkerInfo);
      setOriginalJobChat(jobChat || null);
      if (id && jobChat && jobChat.unreadMessages > 0) {
        // Only call read endpoint if there are unread messages
        // Cache clearing and query invalidation handled in onSettled
        openChatMutation.mutate(id, { onSuccess });
      }
    },
    [openChatMutation]
  );

  const markAsRead = useCallback(
    (chatId: number, onSuccess?: () => void) => {
      if (!readMessagesMutation.isPending) {
        readMessagesMutation.mutate(chatId, { onSuccess });
      }
    },
    [readMessagesMutation]
  );

  const markAsUnread = useCallback(
    (chatId: number, onSuccess?: () => void) => {
      if (!unreadMessagesMutation.isPending) {
        unreadMessagesMutation.mutate(chatId, { onSuccess });
      }
    },
    [unreadMessagesMutation]
  );

  const batchMarkAsRead = useCallback(
    (chatIds: number[], onSuccess?: () => void) => {
      if (chatIds.length === 0 || batchReadMessagesMutation.isPending) return;
      batchReadMessagesMutation.mutate(chatIds, { onSuccess });
    },
    [batchReadMessagesMutation]
  );

  const contextValue: IChatContext = useMemo(() => {
    return {
      chatPerson,
      chatAssignmentId,
      jobChatId,
      showPrivateInfo,
      chatWasCreated,
      setChatAssignmentId,
      setJobChatId,
      setChatWasCreated: handleSetChatWasCreated,
      openChat,
      markAsRead,
      markAsUnread,
      batchMarkAsRead,
      clearChatData,
      messages,
      systemMessages,
      unsentMessages,
      orderedMessages,
      setMessages,
      setSystemMessages,
      setUnsentMessages,
      originalJobMessage: originalJobChat,
    };
  }, [
    chatPerson,
    chatAssignmentId,
    jobChatId,
    showPrivateInfo,
    chatWasCreated,
    handleSetChatWasCreated,
    openChat,
    markAsRead,
    markAsUnread,
    batchMarkAsRead,
    clearChatData,
    messages,
    systemMessages,
    unsentMessages,
    orderedMessages,
    originalJobChat,
  ]);

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

export function useChatContext() {
  const context: IChatContext = useContext(ChatContext);
  if (context === undefined) {
    throw new Error(
      'useChatContext must be used within a ChatProvider. Wrap a parent component in <ChatProvider> to fix this error.'
    );
  }
  return context;
}
